// Knowledge base articles. Long-form content for SEO.
// Each article includes a structured body with sections, lists, FAQ.

export const KENNISBANK = {
  "paarden-gezondheid": {
    slug: "paarden-gezondheid",
    category: "Gezondheid",
    title: "Paardengezondheid — complete gids",
    lead:
      "Paardengezondheid begint bij goed observeren, structureel dossier voeren en op tijd professionals inschakelen. In deze gids leggen we de basis uit: dagelijkse signalen, controles, preventie en wanneer je de dierenarts belt.",
    datePublished: "2026-05-01",
    sections: [
      {
        h: "Wat is paardengezondheid?",
        p: "Paardengezondheid is meer dan 'niet ziek zijn'. Het gaat om een goed werkend bewegingsapparaat, een gezonde spijsvertering, mentale balans en regelmatige preventieve zorg. EquiSight ondersteunt al deze pijlers in één dossier per paard.",
      },
      {
        h: "Dagelijkse signalen",
        list: [
          "Eetlust en watergebruik",
          "Gedrag in de stal en op de weide",
          "Mest (consistentie, frequentie)",
          "Houding en beweging",
          "Vacht en huidconditie",
          "Hartslag en ademhaling in rust",
        ],
      },
      {
        h: "Preventief beheer",
        p: "Goede paardengezondheid is grotendeels preventie. Houd vaccinaties, ontworming, gebitscontroles en hoefverzorging consistent bij. EquiSight stuurt automatisch herinneringen.",
      },
      {
        h: "Wanneer bel je de dierenarts?",
        list: [
          "Niet eten of drinken (>4 uur)",
          "Koliek-verschijnselen",
          "Plotselinge kreupelheid",
          "Hoge koorts (>38,5°C)",
          "Wonden die niet stoppen met bloeden",
          "Bevallingsproblemen",
        ],
      },
    ],
    faq: [
      {
        q: "Hoe vaak moet een paard naar de tandarts?",
        a: "Eén keer per jaar bij volwassen paarden. Jonge paarden (3–5 jaar) en senioren vaker.",
      },
      {
        q: "Welke vaccinaties zijn verplicht?",
        a: "Voor KNHS wedstrijden is influenzavaccinatie volgens schema verplicht. Tetanus wordt sterk aangeraden.",
      },
    ],
    related: ["entingen", "voeradvies", "hoefverzorging"],
  },

  voeradvies: {
    slug: "voeradvies",
    category: "Voeding",
    title: "Voeradvies paard — basisregels en valkuilen",
    lead:
      "Goede voeding is de basis van paardengezondheid. In deze gids: hooi, krachtvoer, supplementen, gewicht en de fructaan-index uitgelegd.",
    datePublished: "2026-05-02",
    sections: [
      {
        h: "Hoeveel hooi heeft een paard nodig?",
        p: "Een gemiddeld paard heeft 1,5–2% van zijn lichaamsgewicht aan ruwvoer (hooi/kuil) per dag nodig. Voor een 500kg paard is dat 7,5–10kg hooi.",
      },
      {
        h: "Krachtvoer — wanneer wel?",
        p: "Krachtvoer is alleen nodig bij intensief werk, dracht, lactatie of bijzondere energiebehoefte. Veel paarden krijgen onnodig krachtvoer, wat tot overgewicht en metabole problemen leidt.",
      },
      {
        h: "Supplementen",
        list: [
          "Zout/mineralen (gratis ter beschikking)",
          "Magnesium bij stress / spieren",
          "Biotine bij hoefproblemen",
          "Glucosamine bij gewrichten",
        ],
      },
      {
        h: "Fructaan-index — pas op met gras",
        p: "Vooral 's ochtends in het voorjaar en bij koude nachten + zonnige dagen is het fructaangehalte in gras hoog. Risicovol voor hoefbevangenheid. EquiSight waarschuwt automatisch.",
      },
    ],
    faq: [
      {
        q: "Moet ik altijd krachtvoer geven?",
        a: "Nee. Veel paarden hebben genoeg aan goed ruwvoer en een mineraalsupplement. Krachtvoer alleen bij meer energiebehoefte.",
      },
      {
        q: "Hoe weet ik of mijn paard overgewicht heeft?",
        a: "Conditiescore (BCS) en meetband. EquiSight helpt je het bij te houden en geeft trend-waarschuwingen.",
      },
    ],
    related: ["fructaan-index", "paarden-gezondheid"],
  },

  hoefverzorging: {
    slug: "hoefverzorging",
    category: "Hoefverzorging",
    title: "Hoefverzorging paard — wat je moet weten",
    lead:
      "Geen voet, geen paard. Hoefverzorging vraagt om consistentie: bekappen of beslag, regelmatige controle, droge stal en goede voeding.",
    datePublished: "2026-05-03",
    sections: [
      {
        h: "Hoe vaak moet de hoefsmid komen?",
        p: "Gemiddeld iedere 6–8 weken. Bij beslag iets korter, barhoof kan soms langer. Houd het regelmatig bij — EquiSight stuurt automatisch herinneringen.",
      },
      {
        h: "Beslag of barhoof?",
        p: "Beide kan goed, afhankelijk van het paard, de discipline en de stal/weide-omstandigheden. Overleg met je hoefsmid.",
      },
      {
        h: "Signalen van hoefproblemen",
        list: [
          "Onregelmatig lopen",
          "Pulserende slagaders",
          "Warmte in de hoef",
          "Scheuren of brokkelen",
          "Stankige rotstraal",
        ],
      },
    ],
    faq: [
      {
        q: "Wat doet een hoefsmid precies?",
        a: "Bekappen (corrigeren en gladstrijken van de hoef), eventueel ijzers aanpassen of nieuwe leggen, en de algemene hoefgezondheid beoordelen.",
      },
    ],
    related: ["paarden-gezondheid"],
  },

  entingen: {
    slug: "entingen",
    category: "Gezondheid",
    title: "Entingen paard — schema, soorten en regels",
    lead:
      "Welke entingen heeft een paard nodig? Influenza, tetanus, rhinopneumonie en west-nijl. Schema's en KNHS regels op een rij.",
    datePublished: "2026-05-04",
    sections: [
      {
        h: "Influenza",
        p: "Eerste serie van 3 vaccinaties (week 0, 4, 6 maanden), daarna jaarlijks. Voor KNHS wedstrijden verplicht en max 6 maanden geleden gevaccineerd.",
      },
      {
        h: "Tetanus",
        p: "Eerste serie van 2 (week 0 en 4 weken), daarna iedere 2–3 jaar. Sterk aanbevolen voor ieder paard.",
      },
      {
        h: "Rhinopneumonie (EHV-1/4)",
        p: "Vooral bij stalruimtes met veel paarden en bij drachtige merries. Iedere 6 maanden.",
      },
      {
        h: "West-Nijl virus",
        p: "Aanbevolen in risicogebieden. Jaarlijks, vóór muggentijd.",
      },
    ],
    faq: [
      {
        q: "Wat als mijn paard te laat is gevaccineerd?",
        a: "Bij influenza moet je vaak opnieuw beginnen met de basisserie. EquiSight waarschuwt op tijd.",
      },
    ],
    related: ["paarden-gezondheid"],
  },

  "fructaan-index": {
    slug: "fructaan-index",
    category: "Voeding",
    title: "Fructaan-index paard — wat het is en hoe je het gebruikt",
    lead:
      "Wat is de fructaan-index? Wanneer is gras gevaarlijk voor je paard? Hoe voorkom je hoefbevangenheid met data en planning.",
    datePublished: "2026-05-05",
    sections: [
      {
        h: "Wat is fructaan?",
        p: "Fructaan is een suiker die gras opslaat. Bij hoge concentraties kan dit leiden tot fermentatie in de dikke darm en daarmee tot hoefbevangenheid (laminitis).",
      },
      {
        h: "Wanneer is fructaan hoog?",
        list: [
          "Koude nachten + zonnige dagen (lente, herfst)",
          "Vroege ochtend",
          "Na een nachtvorst",
          "Gestreste gras (na maaien, droogte, kort)",
        ],
      },
      {
        h: "Hoe houd je het bij?",
        p: "EquiSight berekent de fructaan-index per weide en waarschuwt bij hoog risico. Combineer met weidegang-tijden voor optimaal beheer.",
      },
    ],
    faq: [
      {
        q: "Welke paarden zijn extra risicovol?",
        a: "Pony's (vooral Shetlanders en Welsh), te zware paarden, EMS- en Cushing-paarden, en eerder bevangen paarden.",
      },
    ],
    related: ["voeradvies", "paarden-gezondheid"],
  },

  koliek: {
    slug: "koliek",
    category: "Aandoeningen",
    title: "Koliek paard — symptomen, oorzaken & wat te doen",
    lead:
      "Koliek is buikpijn. Bij paarden is het altijd ernstig — bel direct de dierenarts bij twijfel. Lees hier symptomen, oorzaken en eerste hulp.",
    datePublished: "2026-05-06",
    sections: [
      {
        h: "Symptomen",
        list: [
          "Niet eten of drinken",
          "Rusteloos, krabben in de grond",
          "Liggen en weer opstaan",
          "Naar de buik kijken of trappen",
          "Zweten zonder inspanning",
          "Verhoogde hartslag (>48/min)",
        ],
      },
      {
        h: "Oorzaken",
        list: [
          "Voederwisselingen",
          "Onvoldoende water (vooral 's winters)",
          "Te veel krachtvoer",
          "Zand-opname",
          "Stress",
          "Worminfectie",
        ],
      },
      {
        h: "Wat te doen?",
        p: "Bel direct de dierenarts. Laat het paard niet rollen als het zwaar koliek heeft. Wandel rustig als dat lukt. Voorkom voer en water tot de dierenarts er is.",
      },
    ],
    faq: [
      {
        q: "Kan ik koliek voorkomen?",
        a: "Voor een deel: stabiel ruwvoer, voldoende water, voldoende beweging, regelmatige ontworming en geen plotselinge voederwisselingen.",
      },
    ],
    related: ["paarden-gezondheid", "voeradvies"],
  },

  droes: {
    slug: "droes",
    category: "Aandoeningen",
    title: "Droes paard — symptomen, besmetting & isolatie",
    lead:
      "Droes (strangles) is zeer besmettelijk. Snelle herkenning en isolatie zijn cruciaal om uitbraak op stal te voorkomen.",
    datePublished: "2026-05-07",
    sections: [
      {
        h: "Symptomen",
        list: [
          "Koorts (>39°C)",
          "Geen eetlust",
          "Zwellingen onder de kaak",
          "Etterende neusuitvloeiing",
          "Abcessen die openbarsten",
        ],
      },
      {
        h: "Isolatie",
        p: "Verdacht paard direct in quarantaine. Aparte verzorging, aparte materialen, hygiëne strikt. Andere paarden screenen.",
      },
    ],
    faq: [
      {
        q: "Hoelang duurt droes?",
        a: "Meestal 2–6 weken. Sommige paarden blijven dragers via de luchtzakken — laat onderzoeken.",
      },
    ],
    related: ["paarden-gezondheid"],
  },

  zomereczeem: {
    slug: "zomereczeem",
    category: "Aandoeningen",
    title: "Zomereczeem paard — preventie & behandeling",
    lead:
      "Zomereczeem is een allergische reactie op het speeksel van kriebelmugjes (Culicoides). Lees over preventie en behandeling.",
    datePublished: "2026-05-08",
    sections: [
      {
        h: "Symptomen",
        list: [
          "Jeuk, vooral manenkam en staartwortel",
          "Kale plekken en korstjes",
          "Onrust 's avonds en 's ochtends",
        ],
      },
      {
        h: "Preventie",
        list: [
          "Eczeemdeken met buikflap en kapje",
          "Stal in op piekuren (zonsondergang / -opgang)",
          "Insectenwerend middel",
          "Ventilator op stal",
          "Verre van waterpartijen",
        ],
      },
    ],
    faq: [
      {
        q: "Helpt voeding tegen zomereczeem?",
        a: "Sommige supplementen (omega-3, vitamine E) ondersteunen huid en immuniteit, maar genezen het niet.",
      },
    ],
    related: ["paarden-gezondheid"],
  },
};

export default KENNISBANK;
